import Comments from '@components/comments';
import Holder from '@components/holder';
import Process from '@components/process';
import PumpChart from '@components/pump-chart';
import PumpHistory from '@components/pump-history';
import type { PoolFields } from '@libs/pump';
import { useState, type FC } from 'react';
import type { OpenapiClientPump } from 'src/openapi/pump';
import styles from './mobile-page.module.scss';
import { formatContract } from '@utils/format-contract';
import { timeDifference } from '@utils/time-difference';
import Avatar from '@components/avatar';
import { Link } from 'react-router-dom';
import { globalSettingModel } from '@models/global-setting.model';
import { useSuiNSName } from '@hooks/use-sui-ns-name';
import copyIcon from '@assets/images/pump/copy.svg';
import { toast } from 'react-toastify';
import { useCopyToClipboard } from 'react-use';
import commentsIcon from '@assets/images/pump/mobile-comments.svg';
import historyIcon from '@assets/images/pump/mobile-history.svg';
import infoIcon from '@assets/images/pump/mobile-info.svg';
import { bigNumberToReadable } from '@utils/big-number-to-readable';
import FormatZeroDecimal from '@components/format-zero-decimal';
import ContactType from '@components/contact-type';
const tabs = [
  {
    label: 'Info',
    value: 'info',
    icon: infoIcon,
  },
  {
    label: 'Thread',
    value: 'thread',
    icon: commentsIcon,
  },
  {
    label: 'Trades',
    value: 'trades',
    icon: historyIcon,
  },
];

type TabValue = (typeof tabs)[number]['value'];

const MobilePage: FC<{
  pool: OpenapiClientPump.GetPoolsByTokenAddressResponse;
  poolFields: PoolFields;
  latestPrice: OpenapiClientPump.GetTradeLatestPriceResponse | undefined;
}> = ({ pool, poolFields, latestPrice }) => {
  const suiNames = useSuiNSName(pool?.created_by || '');
  const [, copy] = useCopyToClipboard();
  const [tab, setTab] = useState<TabValue>('info');
  return (
    <div className={styles.wrapper}>
      <div className={styles.mobile_line}>
        <div className={styles.name}>
          {pool.name} (${pool.symbol})
        </div>
        <div className={styles.avatar}>
          <Avatar address={pool.created_by} />
          <Link to={globalSettingModel.explorerUrlForAccount(pool.created_by)} target="_blank">
            {suiNames || pool.created_by.slice(pool.created_by.length - 8)}
          </Link>
        </div>
      </div>
      <div className={styles.mobile_line}>
        <div className={styles.copy}>
          CA: <span>{`${formatContract(pool.token_address)}`}</span>
          <img
            src={copyIcon}
            onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
              event.stopPropagation();
              copy(pool.token_address);
              toast('Address is copied');
            }}
          />
        </div>
        <div className={styles.time}>{timeDifference(pool.deployed_at, 1)} ago</div>
      </div>

      <div className={styles.market}>
        <div>Marketcap:</div>
        <div>
          {pool.market_cap_usd
            ? `$${bigNumberToReadable(pool.market_cap_usd.toString(), 0, true, 2)}`
            : '-'}
        </div>
      </div>

      <PumpChart pool={pool} />
      <ul className={styles.tab}>
        {tabs.map((item) => (
          <li
            onClick={() => setTab(item.value)}
            className={item.value === tab ? styles.active : ''}
            key={item.value}
          >
            <img src={item.icon} />
            {item.label}
          </li>
        ))}
      </ul>

      {tab === 'info' && (
        <>
          <div className={styles.first}>
            <div className={styles.token_name}>
              {pool.name} (${pool.symbol})
            </div>
            <div className={styles.first_main}>
              <div className={styles.icon}>
                <img src={pool.uri} />
              </div>

              <div className={styles.content}>
                <div className={styles.description}>{pool.description}</div>
                <div className={styles.title}>
                  <ContactType
                    website={pool.website}
                    twitter={pool.twitter}
                    telegram={pool.telegram}
                    className={styles.contact}
                  />
                </div>
              </div>
            </div>
            <div className={styles.data}>
              <div className={styles.line}>
                <p>Price</p>
                <p>
                  {pool.token_price ? (
                    <>
                      {pool.is_completed && '$'}
                      <FormatZeroDecimal
                        value={bigNumberToReadable(
                          pool.is_completed
                            ? latestPrice
                              ? latestPrice.price_usd
                              : pool.token_price_usd
                            : latestPrice
                            ? latestPrice.price_sui
                            : pool.token_price,
                          0,
                        )}
                      />{' '}
                      {!pool.is_completed && '$SUI'}
                    </>
                  ) : (
                    '-'
                  )}
                </p>
              </div>
              <div className={styles.line}>
                <p>Market Cap</p>
                <p>
                  {pool.market_cap_usd
                    ? `$${bigNumberToReadable(pool.market_cap_usd.toString(), 0, true, 2)}`
                    : '-'}
                </p>
              </div>
              <div className={styles.line}>
                <p>24H Volume</p>
                <p>
                  {!pool.is_completed && pool.volume_24h
                    ? `${bigNumberToReadable(pool.volume_24h?.toString(), 0, true)} $SUI`
                    : pool.is_completed && pool.volume_24h_usd
                    ? `$${bigNumberToReadable(pool.volume_24h_usd?.toString(), 0, true)}`
                    : '-'}
                </p>
              </div>
            </div>
          </div>
          {!pool.clmm_pool_id && <Process pool={pool} poolFields={poolFields} />}
          <Holder pool={pool} />
        </>
      )}

      {tab === 'thread' && <Comments pool={pool} />}
      {tab === 'trades' && <PumpHistory pool={pool} />}
    </div>
  );
};

export default MobilePage;
